import React, { useEffect, useState, useContext } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Switch, DatePicker, InputNumber, Divider,
  Space, Tag, Popconfirm, Typography, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { Title, Text } = Typography;

export default function EmployeeJobsAdminPage() {
  const currentUser       = useContext(UserContext);
  const isAdmin           = currentUser?.Role === 'admin';
  const canViewFinancials = (currentUser?.CanViewFinancials ?? 0);
  const [jobs, setJobs]               = useState([]);
  const [paycorNames, setPaycorNames] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form]                        = Form.useForm();
  const [saving, setSaving]           = useState(false);

  const [adminTeams, setAdminTeams]     = useState([]);
  const [costs, setCosts]               = useState([]);
  const [costsLoading, setCostsLoading] = useState(false);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [editingCost, setEditingCost]   = useState(null);
  const [costForm]                      = Form.useForm();
  const [costSaving, setCostSaving]     = useState(false);

  const loadJobs = () => {
    setLoading(true);
    api.get('/api/jobs')
      .then((r) => setJobs(r.data))
      .catch(() => message.error('Failed to load jobs'))
      .finally(() => setLoading(false));
  };

  const loadPaycorNames = () => {
    api.get('/api/jobs/paycor-names')
      .then((r) => setPaycorNames(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
  };

  const loadCosts = () => {
    setCostsLoading(true);
    api.get('/api/jobs/costs')
      .then((r) => setCosts(r.data))
      .catch(() => message.error('Failed to load cost rates'))
      .finally(() => setCostsLoading(false));
  };

  const loadAdminTeams = () => {
    api.get('/api/teams').then((r) => setAdminTeams(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadJobs();
    loadPaycorNames();
    loadAdminTeams();
  }, []);

  useEffect(() => {
    loadCosts();
  }, []); // eslint-disable-line

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ IsActive: true, InAdherence: true, IncludeInStats: true, PaycorNames: [] });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      JobName:        row.JobName,
      PaycorNames:    row.PaycorNames || [],
      IsActive:       row.IsActive,
      InAdherence:    row.InAdherence,
      IncludeInStats: row.IncludeInStats,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/jobs/${editing.JobID}`, values);
        message.success('Job updated');
      } else {
        await api.post('/api/jobs', values);
        message.success('Job created');
      }
      setModalOpen(false);
      loadJobs();
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openAddCost = () => {
    setEditingCost(null);
    costForm.resetFields();
    setCostModalOpen(true);
  };

  const openEditCost = (row) => {
    setEditingCost(row);
    costForm.setFieldsValue({
      JobID:             row.JobID,
      TeamID:            row.TeamID ?? null,
      FromDate:          row.FromDate ? dayjs(row.FromDate) : null,
      ToDate:            row.ToDate   ? dayjs(row.ToDate)   : null,
      TrainingWage:      row.TrainingWage,
      HireCost:          row.HireCost,
      BillableRate:      row.BillableRate,
      BillableAfterDays: row.BillableAfterDays,
    });
    setCostModalOpen(true);
  };

  const handleSaveCost = async () => {
    let values;
    try { values = await costForm.validateFields(); } catch { return; }
    setCostSaving(true);
    const payload = {
      JobID:             values.JobID,
      TeamID:            values.TeamID ?? null,
      FromDate:          values.FromDate ? values.FromDate.format('YYYY-MM-DD') : null,
      ToDate:            values.ToDate   ? values.ToDate.format('YYYY-MM-DD')   : null,
      TrainingWage:      values.TrainingWage,
      HireCost:          values.HireCost ?? 0,
      BillableRate:      values.BillableRate ?? null,
      BillableAfterDays: values.BillableAfterDays ?? null,
    };
    try {
      if (editingCost) {
        await api.put(`/api/jobs/costs/${editingCost.CostID}`, payload);
        message.success('Cost rate updated');
      } else {
        await api.post('/api/jobs/costs', payload);
        message.success('Cost rate created');
      }
      setCostModalOpen(false);
      loadCosts();
    } catch {
      message.error('Save failed');
    } finally {
      setCostSaving(false);
    }
  };

  const handleDeleteCost = async (costId) => {
    try {
      await api.delete(`/api/jobs/costs/${costId}`);
      message.success('Deleted');
      loadCosts();
    } catch {
      message.error('Delete failed');
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await api.delete(`/api/jobs/${jobId}`);
      message.success('Deleted');
      loadJobs();
    } catch {
      message.error('Delete failed — job may be referenced by forecasts');
    }
  };

  const columns = [
    {
      title: 'Job ID',
      dataIndex: 'JobID',
      key: 'JobID',
      width: 90,
      sorter: (a, b) => (a.JobID ?? 0) - (b.JobID ?? 0),
    },
    {
      title: 'Job Name',
      dataIndex: 'JobName',
      key: 'JobName',
      sorter: (a, b) => a.JobName.localeCompare(b.JobName),
    },
    {
      title: 'Paycor Job Names',
      dataIndex: 'PaycorNames',
      key: 'PaycorNames',
      render: (names) =>
        names && names.length > 0
          ? names.map((n) => <Tag key={n}>{n}</Tag>)
          : <Text type="secondary">Not mapped</Text>,
    },
    {
      title: 'In Adherence',
      dataIndex: 'InAdherence',
      key: 'InAdherence',
      width: 110,
      render: (v) => <Tag color={v ? 'blue' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'In Stats',
      dataIndex: 'IncludeInStats',
      key: 'IncludeInStats',
      width: 90,
      render: (v) => <Tag color={v ? 'purple' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'IsActive',
      key: 'IsActive',
      width: 100,
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    ...(isAdmin ? [{
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete this job?"
            description="Any forecasts using this job will lose their job reference."
            onConfirm={() => handleDelete(row.JobID)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const costColumns = [
    {
      title: 'Job', dataIndex: 'JobName', key: 'JobName',
      sorter: (a, b) => a.JobName.localeCompare(b.JobName),
    },
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 150,
      render: (v) => v || <Text type="secondary">Any</Text>,
    },
    { title: 'From', dataIndex: 'FromDate', key: 'FromDate', width: 110 },
    {
      title: 'To', dataIndex: 'ToDate', key: 'ToDate', width: 110,
      render: (v) => v || <Text type="secondary">Open</Text>,
    },
    {
      title: 'Training Wage', dataIndex: 'TrainingWage', key: 'TrainingWage', width: 130, align: 'right',
      render: (v) => v != null ? `$${Number(v).toFixed(2)}/hr` : '—',
    },
    {
      title: 'Hire Cost', dataIndex: 'HireCost', key: 'HireCost', width: 110, align: 'right',
      render: (v) => v != null ? `$${Number(v).toFixed(2)}` : '—',
    },
    {
      title: 'Billable Rate', dataIndex: 'BillableRate', key: 'BillableRate', width: 120, align: 'right',
      render: (v) => v != null ? `$${Number(v).toFixed(2)}/hr` : '—',
    },
    {
      title: 'Billable After', dataIndex: 'BillableAfterDays', key: 'BillableAfterDays', width: 115, align: 'right',
      render: (v) => v != null ? `Day ${v}` : '—',
    },
    ...(isAdmin ? [{
      title: 'Actions', key: 'actions', width: 90,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditCost(row)} />
          <Popconfirm
            title="Delete this rate?"
            onConfirm={() => handleDeleteCost(row.CostID)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>Employee Jobs</Title>
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add Job
          </Button>
        )}
      </div>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Map internal job names to one or more Paycor job titles. The nightly user sync updates
        each QA user's job automatically based on their current Paycor title.
      </Text>

      <Table
        rowKey="JobID"
        columns={columns}
        dataSource={jobs}
        loading={loading}
        pagination={false}
        size="small"
      />

      <Modal maskClosable={false}
        title={editing ? 'Edit Job' : 'Add Job'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="JobName"
            label="Job Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. Agent, Support Agent, Supervisor" />
          </Form.Item>

          <Form.Item
            name="PaycorNames"
            label="Paycor Job Names"
            extra="Select all Paycor titles that map to this job. You can also type a name to add it."
          >
            <Select
              mode="tags"
              showSearch
              allowClear
              placeholder="Select or type Paycor job titles"
              options={paycorNames}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="InAdherence" label="In Adherence" valuePropName="checked"
            extra="When on, this job is pre-selected by default on the Schedule Break & Lunch page.">
            <Switch />
          </Form.Item>

          <Form.Item name="IncludeInStats" label="Include in Stats" valuePropName="checked"
            extra="When off, this job is excluded from the leaderboard, adherence reports, and aggregated stats.">
            <Switch />
          </Form.Item>

          <Form.Item name="IsActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {canViewFinancials >= 2 && (
        <>
          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Title level={5} style={{ margin: 0 }}>Training Cost Rates</Title>
            {isAdmin && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddCost}>
                Add Rate
              </Button>
            )}
          </div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Set the training wage and one-time hire cost per job and date range. Used to calculate training cost impact on the Recruitment Stats page.
          </Text>
          <Table
            rowKey="CostID"
            columns={costColumns}
            dataSource={costs}
            loading={costsLoading}
            pagination={false}
            size="small"
          />

          <Modal maskClosable={false}
            title={editingCost ? 'Edit Cost Rate' : 'Add Cost Rate'}
            open={costModalOpen}
            onOk={handleSaveCost}
            onCancel={() => setCostModalOpen(false)}
            confirmLoading={costSaving}
            okText={editingCost ? 'Save' : 'Create'}
            destroyOnClose
          >
            <Form form={costForm} layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item name="JobID" label="Job" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))}
                  placeholder="Select a job"
                />
              </Form.Item>
              <Form.Item name="TeamID" label="Team (optional)" extra="Leave blank to apply this rate to all teams for the selected job.">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={adminTeams.map((t) => ({ value: t.ID, label: t.Name }))}
                  placeholder="Any team"
                />
              </Form.Item>
              <Form.Item name="FromDate" label="From Date" rules={[{ required: true, message: 'Required' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="ToDate" label="To Date" extra="Leave blank for an open-ended rate.">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="TrainingWage" label="Training Wage ($/hr)" rules={[{ required: true, message: 'Required' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="$" />
              </Form.Item>
              <Form.Item name="HireCost" label="Hire Cost (one-time per person)">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="$" />
              </Form.Item>
              <Form.Item name="BillableRate" label="Billable Rate ($/hr)" extra="Rate charged to the client. Used for Training Return calculations.">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="$" />
              </Form.Item>
              <Form.Item name="BillableAfterDays" label="Billable After (days from start)" extra="Days after class start date when graduates become billable.">
                <InputNumber style={{ width: '100%' }} min={0} precision={0} />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}
    </div>
  );
}
